import { NavLink, useNavigate } from 'react-router-dom'
import {
  BarChart3,
  BookOpen,
  Briefcase,
  CalendarDays,
  CheckSquare,
  Crosshair,
  DollarSign,
  Filter,
  ShieldOff,
  List,
  Image,
  GitBranch,
  Layout,
  Sparkles,
  Share2,
  FileText,
  ChevronsLeft,
  ChevronsRight,
  ClipboardList,
  LayoutDashboard,
  Lightbulb,
  LogOut,
  Mail,
  Map,
  MessageCircle,
  MessageSquare,
  Inbox,
  Package,
  ShoppingCart,
  Phone,
  Radio,
  Settings,
  ShieldCheck,
  Swords,
  Target,
  TrendingUp,
  Users,
  UserSquare2,
  WalletCards,
} from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { cn, initials } from '@/lib/utils'
import { useUIStore } from '@/stores/ui'
import { useAuthStore } from '@/stores/auth'
import { get } from '@/lib/api'
import { useSocketEvent } from '@/lib/socket'
import { Tooltip } from '@/components/ui/Tooltip'
import { Kbd } from '@/components/ui/Kbd'
import { NAV_SHORTCUTS } from '@/hooks/useKeyboardShortcuts'
import { usePermissions, type PermissionKey } from '@/hooks/usePermissions'

type Role = 'SUPER_ADMIN' | 'TENANT_ADMIN' | 'MANAGER' | 'AGENT' | 'READ_ONLY'

interface NavItem {
  to: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  badgeKey?: 'openCases' | 'waitingChats' | 'activeChats'
  /** Force badge colour (default uses brand). */
  badgeTone?: 'urgent' | 'default' | 'live'
  roles?: Role[]
  /** Permission key to gate this item on. SUPER_ADMIN / TENANT_ADMIN bypass. */
  perm?: PermissionKey
}

interface NavSection {
  label: string
  items: NavItem[]
}

export function Sidebar() {
  const collapsed = useUIStore((s) => s.sidebarCollapsed)
  const toggle = useUIStore((s) => s.toggleSidebar)
  const user = useAuthStore((s) => s.user)
  const clearAuth = useAuthStore((s) => s.clear)
  const role = user?.role
  const navigate = useNavigate()
  const { can } = usePermissions()
  const { t } = useTranslation()

  const qc = useQueryClient()
  const { data: usage } = useQuery({
    queryKey: ['tenant', 'usage'],
    queryFn: () => get<{ openCases: number; waitingChats: number; activeChats: number }>('/api/v1/tenant/usage', { background: true }),
    staleTime: 60_000,
    refetchInterval: 60_000,
  })

  // Real-time badge: refetch the usage snapshot whenever a live chat
  // starts, closes, or resolves so the green dot on "Live Chats" reflects
  // what's currently happening without the 60s poll latency.
  useSocketEvent('livechat:new',      () => qc.invalidateQueries({ queryKey: ['tenant', 'usage'] }))
  useSocketEvent('livechat:resolved', () => qc.invalidateQueries({ queryKey: ['tenant', 'usage'] }))
  useSocketEvent('livechat:closed',   () => qc.invalidateQueries({ queryKey: ['tenant', 'usage'] }))

  const sections: NavSection[] = [
    {
      label: t('nav.workspace'),
      items: [
        { to: '/',          label: t('nav.dashboard'),    icon: LayoutDashboard, perm: 'canViewDashboard' },
        { to: '/inbox',     label: t('nav.inbox', 'Inbox'), icon: MessageSquare, perm: 'canViewChannels' },
        { to: '/channels',  label: t('nav.channels'),     icon: Radio,           perm: 'canViewChannels' },
        { to: '/live-chat', label: t('nav.liveChats'),    icon: MessageCircle,   badgeKey: 'waitingChats', badgeTone: 'urgent', perm: 'canViewChannels' },
        { to: '/whatsapp',  label: t('nav.whatsapp'),     icon: MessageSquare,   perm: 'canViewChannels' },
        { to: '/whatsapp/dashboard', label: t('nav.whatsappDashboard', 'WhatsApp Dashboard'), icon: BarChart3, roles: ['TENANT_ADMIN', 'SUPER_ADMIN'] },
        { to: '/email',     label: t('nav.email', 'Email'), icon: Mail,          perm: 'canViewChannels' },
        { to: '/team',      label: t('nav.team'),         icon: Users,           perm: 'canViewTeams' },
        { to: '/tasks',     label: t('nav.tasks', { defaultValue: 'Tasks' }), icon: CheckSquare },
        { to: '/meetings',  label: t('nav.meetings', 'Meetings'), icon: CalendarDays },
        { to: '/calls',     label: t('nav.calls', 'Calls'),       icon: Phone },
      ],
    },
    {
      label: t('nav.customers', { defaultValue: 'Customers' }),
      items: [
        { to: '/cases',     label: t('nav.cases'),        icon: Briefcase,       badgeKey: 'openCases', perm: 'canViewCases' },
        { to: '/queues',    label: t('nav.queues', 'Queues'), icon: Inbox,        perm: 'canViewCases' },
        { to: '/contacts',  label: t('nav.contacts'),     icon: UserSquare2,     perm: 'canViewContacts' },
        { to: '/accounts',  label: t('nav.accounts'),     icon: WalletCards,     perm: 'canViewAccounts' },
        { to: '/customer-assets', label: t('nav.assets', 'Assets'), icon: Package, perm: 'canViewCases' },
        { to: '/orders',    label: t('nav.orders', 'Orders'), icon: ShoppingCart, perm: 'canViewCases' },
        { to: '/contracts', label: t('nav.contracts', 'Contracts'), icon: FileText, perm: 'canViewCases' },
        { to: '/community', label: t('nav.community', 'Community'), icon: MessageSquare, perm: 'canViewCases' },
      ],
    },
    {
      label: t('nav.sales'),
      items: [
        { to: '/leads',         label: t('nav.leads'),         icon: Target,      perm: 'canViewLeads' },
        { to: '/opportunities', label: t('nav.opportunities'), icon: BarChart3,   perm: 'canViewOpportunities' },
        { to: '/forecasting',   label: t('nav.forecasting', 'Forecasting'), icon: TrendingUp, perm: 'canViewOpportunities' },
        { to: '/products',      label: t('nav.products', 'Products'),       icon: Package,    perm: 'canViewOpportunities' },
        { to: '/quotes',        label: t('nav.quotes', 'Quotes'),           icon: FileText,   perm: 'canViewOpportunities' },
        { to: '/price-books',   label: t('nav.pricing', 'Pricing'),         icon: DollarSign, perm: 'canViewOpportunities' },
        { to: '/target-accounts', label: t('nav.targetAccounts', 'Target Accounts'), icon: Crosshair, perm: 'canViewOpportunities' },
        { to: '/territories',   label: t('nav.territories', 'Territories'), icon: Map,        perm: 'canViewOpportunities' },
        { to: '/campaigns',     label: t('nav.campaigns'),     icon: MessageSquare, perm: 'canViewCampaigns' },
        { to: '/segments',      label: t('nav.segments', 'Segments'), icon: Filter, perm: 'canViewCampaigns' },
        { to: '/lists',         label: t('nav.lists', 'Lists'), icon: List, perm: 'canViewCampaigns' },
        { to: '/assets',        label: t('nav.assets', 'Assets'), icon: Image, perm: 'canViewCampaigns' },
        { to: '/journeys',      label: t('nav.journeys', 'Journeys'), icon: GitBranch, perm: 'canViewCampaigns' },
        { to: '/attribution',   label: t('nav.attribution', 'Attribution'), icon: BarChart3, perm: 'canViewCampaigns' },
        { to: '/landing-pages', label: t('nav.landingPages', 'Landing Pages'), icon: Layout, perm: 'canViewCampaigns' },
        { to: '/personalization', label: t('nav.personalization', 'Personalization'), icon: Sparkles, perm: 'canViewCampaigns' },
        { to: '/social',          label: t('nav.social', 'Social'), icon: Share2, perm: 'canViewCampaigns' },
        { to: '/suppressions',  label: t('nav.suppressions', 'Suppressions'), icon: ShieldOff, perm: 'canViewCampaigns', roles: ['TENANT_ADMIN', 'SUPER_ADMIN'] },
        { to: '/intelligence',  label: t('nav.intelligence', 'Intelligence'), icon: Lightbulb, roles: ['TENANT_ADMIN', 'MANAGER', 'SUPER_ADMIN'] },
        { to: '/forms',         label: t('nav.forms', 'Forms'), icon: ClipboardList, roles: ['TENANT_ADMIN', 'MANAGER', 'SUPER_ADMIN'] },
      ],
    },
    {
      label: t('nav.tools'),
      items: [
        { to: '/kb',         label: t('nav.knowledge'),                 icon: BookOpen,    perm: 'canViewKb' },
        { to: '/macros',     label: t('nav.macros', 'Macros'),          icon: ClipboardList, perm: 'canViewCases' },
        { to: '/proactive',  label: t('nav.proactive'),                 icon: Radio,       perm: 'canViewChannels' },
        { to: '/bot/transcripts', label: t('nav.botTranscripts', 'Bot Transcripts'), icon: MessageSquare, perm: 'canViewReports' },
        { to: '/reports',    label: t('nav.reports'),                   icon: BarChart3,   perm: 'canViewReports' },
        { to: '/reports/service-insights', label: t('nav.serviceInsights', 'Service Insights'), icon: Lightbulb, perm: 'canViewReports' },
        { to: '/service-ai', label: t('nav.serviceAi', 'Service AI'), icon: Sparkles, roles: ['TENANT_ADMIN', 'MANAGER', 'SUPER_ADMIN'] },
        { to: '/reports/win-loss', label: t('nav.winLoss', 'Win/Loss'), icon: Swords,      perm: 'canViewReports' },
        { to: '/compliance', label: t('nav.compliance', 'Compliance'),  icon: ShieldCheck, perm: 'canViewSettings', roles: ['TENANT_ADMIN', 'SUPER_ADMIN'] },
        { to: '/settings',   label: t('nav.settings'),                  icon: Settings,    perm: 'canViewSettings', roles: ['TENANT_ADMIN', 'SUPER_ADMIN'] },
        { to: '/admin',      label: 'Admin',                            icon: ShieldCheck, roles: ['SUPER_ADMIN'] },
      ],
    },
  ]

  function signOut() {
    clearAuth()
    navigate('/login')
  }

  const userName = user ? `${user.firstName} ${user.lastName}` : ''
  const roleLabel = user?.role.replace(/_/g, ' ').toLowerCase() ?? ''

  return (
    <aside
      className={cn(
        'hidden shrink-0 flex-col border-r border-white/10 bg-[var(--vela-deep-navy)] shadow-[inset_-1px_0_0_rgba(255,255,255,0.04)] transition-[width] duration-200 ease-out md:flex',
        collapsed ? 'w-14' : 'w-[228px]',
      )}
      aria-label="Primary navigation"
    >
      {/* === Logo ============================================================ */}
      <div className={cn('flex h-16 items-center border-b border-white/10', collapsed ? 'justify-center px-2' : 'px-4')}>
        {collapsed ? (
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--vela-primary)] text-[15px] font-bold text-white shadow-[0_10px_24px_rgba(79,140,255,0.28)]">
            V
          </div>
        ) : (
          <div className="flex min-w-0 items-center gap-2.5">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[var(--vela-primary)] text-[15px] font-bold text-white shadow-[0_10px_24px_rgba(79,140,255,0.28)]">
              V
            </div>
            <div className="min-w-0 leading-tight">
              <div className="text-[17px] font-semibold tracking-[-0.02em] text-white">Vela</div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/[0.45]">by Altream</div>
            </div>
          </div>
        )}
      </div>

      {/* === Nav sections ==================================================== */}
      <nav className="flex-1 overflow-y-auto py-3 [scrollbar-color:rgba(255,255,255,0.22)_transparent]">
        {sections.map((section) => {
          const visible = section.items.filter((i) => {
            if (i.roles && !(role && i.roles.includes(role))) return false
            // Permission check: missing `perm` means the item is always shown
            // (e.g. Admin link which is already role-gated). `can()` returns
            // true for TENANT_ADMIN/SUPER_ADMIN regardless.
            if (i.perm && !can(i.perm)) return false
            return true
          })
          if (visible.length === 0) return null
          return (
            <div key={section.label} className="mb-1">
              {!collapsed ? (
                <div className="px-4 pb-1.5 pt-5 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-white/[0.38]">
                  {section.label}
                </div>
              ) : (
                <div className="mx-3 my-3 h-px bg-white/10" />
              )}
              {visible.map((item) => {
                const badge =
                  item.badgeKey === 'openCases'    && usage?.openCases    ? usage.openCases :
                  item.badgeKey === 'waitingChats' && usage?.waitingChats ? usage.waitingChats :
                  item.badgeKey === 'activeChats'  && usage?.activeChats  ? usage.activeChats :
                  null
                const urgent = item.badgeTone === 'urgent'
                const live   = item.badgeTone === 'live'
                const link = (
                  <NavLink
                    to={item.to}
                    end={item.to === '/'}
                    className={({ isActive }) =>
                      cn(
                        'group flex items-center transition-colors duration-150',
                        collapsed
                          ? 'relative mx-auto h-9 w-9 justify-center rounded-lg'
                          : 'mx-2 gap-2.5 rounded-lg px-3 py-[7px] text-[13px]',
                        isActive
                          ? 'bg-[rgba(79,140,255,0.16)] font-semibold text-white shadow-[inset_0_0_0_1px_rgba(79,140,255,0.20)]'
                          : 'text-white/70 hover:bg-white/[0.06] hover:text-white',
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <item.icon
                          className={cn(
                            'h-[18px] w-[18px] shrink-0',
                            isActive ? 'text-[var(--vela-primary)]' : 'text-white/40 group-hover:text-white/[0.72]',
                          )}
                        />
                        {/* Collapsed sidebar: badge count won't fit, so surface
                            a red dot when chats are waiting for an agent. */}
                        {collapsed && badge ? (
                          <span
                            aria-hidden
                            className="absolute right-1 top-1 inline-block h-2 w-2 rounded-full bg-neg"
                            style={{ boxShadow: '0 0 0 2px var(--vela-deep-navy)' }}
                          />
                        ) : null}
                        {!collapsed && (
                          <>
                            <span className="flex-1 truncate">
                              {item.label}
                            </span>
                            {badge ? (
                              <span
                                className={cn(
                                  'ml-auto inline-flex items-center gap-1 rounded-full px-1.5 py-[1px] text-[10px] font-semibold tabular-nums',
                                  urgent
                                    ? 'bg-[rgba(229,72,77,0.16)] text-[#FFB4B7]'
                                    : live
                                    ? 'bg-[rgba(79,140,255,0.16)] text-[#BFD6FF]'
                                    : 'bg-white/10 text-white/72',
                                )}
                              >
                                {live ? (
                                  <span
                                    aria-hidden
                                    className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--vela-primary)]"
                                    style={{ boxShadow: '0 0 0 2px rgba(79,140,255,0.18)' }}
                                  />
                                ) : null}
                                {badge}
                              </span>
                            ) : (
                              (() => {
                                const sc = NAV_SHORTCUTS.find((n) => n.path === item.to)
                                if (!sc) return null
                                return (
                                  <span
                                    className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-0.5"
                                    aria-hidden
                                  >
                                    <Kbd>G</Kbd>
                                    <Kbd>{sc.key.toUpperCase()}</Kbd>
                                  </span>
                                )
                              })()
                            )}
                          </>
                        )}
                      </>
                    )}
                  </NavLink>
                )
                return collapsed ? (
                  <Tooltip key={item.to} label={item.label} side="right">{link}</Tooltip>
                ) : (
                  <div key={item.to}>{link}</div>
                )
              })}
            </div>
          )
        })}
      </nav>

      {/* === Footer ========================================================== */}
      <div className="border-t border-white/10 px-3 py-3">
        {user ? (
          collapsed ? (
            <Tooltip label={`${userName} · ${t('auth.signOut')}`} side="right">
              <button
                onClick={signOut}
                className="mx-auto flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-sm font-semibold text-white ring-1 ring-white/10 transition-colors hover:bg-white/[0.15]"
                aria-label={`Sign out ${userName}`}
              >
                {initials(userName)}
              </button>
            </Tooltip>
          ) : (
            <div className="flex items-center gap-2.5 rounded-lg px-1.5 py-1">
              <span
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/10 text-sm font-semibold text-white ring-1 ring-white/10"
                aria-hidden
              >
                {initials(userName)}
              </span>
              <div className="min-w-0 flex-1 leading-tight">
                <div className="truncate text-[13px] font-semibold text-white">{userName}</div>
                <div className="truncate text-[11px] capitalize text-white/[0.45]">{roleLabel}</div>
              </div>
              <Tooltip label={t('auth.signOut')}>
                <button
                  onClick={signOut}
                  className="flex h-7 w-7 items-center justify-center rounded-md text-white/[0.45] transition-colors hover:bg-white/[0.06] hover:text-white"
                  aria-label={t('auth.signOut')}
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </Tooltip>
            </div>
          )
        ) : null}

        <button
          onClick={toggle}
          className={cn(
            'mt-1.5 flex h-7 w-full items-center gap-1.5 rounded-md text-xs text-white/[0.45] transition-colors hover:bg-white/[0.06] hover:text-white',
            collapsed ? 'justify-center px-0' : 'px-2.5',
          )}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronsRight className="h-3.5 w-3.5" />
          ) : (
            <>
              <ChevronsLeft className="h-3.5 w-3.5" /> {t('actions.collapse')}
            </>
          )}
        </button>
      </div>
    </aside>
  )
}
